import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, LogOut, Trash2, Download } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang, PLANS, money } from "@/lib/i18n";
import { http, formatApiError } from "@/lib/api";
import { LangSwitch } from "@/components/Nav";
import { Wordmark } from "@/components/Logo";

const L = {
  fr: { title: "Demandes d'adhésion", login: "Connexion admin", email: "Courriel", pw: "Mot de passe", enter: "Se connecter", out: "Déconnexion", back: "Site", total: "Demandes", value: "Valeur 36 mois", top: "Forfait le plus demandé", empty: "Aucune demande pour l'instant.", cols: ["Date", "Client", "Forfait", "Formule", "Statut", ""], status: { new: "Nouveau", processing: "En traitement", confirmed: "Confirmé" }, bad: "Identifiants invalides" },
  en: { title: "Sign-up requests", login: "Admin login", email: "Email", pw: "Password", enter: "Sign in", out: "Sign out", back: "Site", total: "Requests", value: "36-month value", top: "Most requested plan", empty: "No requests yet.", cols: ["Date", "Client", "Plan", "Option", "Status", ""], status: { new: "New", processing: "Processing", confirmed: "Confirmed" }, bad: "Invalid credentials" },
  pt: { title: "Solicitações de adesão", login: "Login admin", email: "E-mail", pw: "Senha", enter: "Entrar", out: "Sair", back: "Site", total: "Solicitações", value: "Valor 36 meses", top: "Plano mais pedido", empty: "Nenhuma solicitação ainda.", cols: ["Data", "Cliente", "Plano", "Modalidade", "Status", ""], status: { new: "Novo", processing: "Em andamento", confirmed: "Confirmado" }, bad: "Credenciais inválidas" },
};

const planOf = (id) => PLANS.find((p) => p.id === id);
const value36 = (r) => (r.plan ? (r.commitment === "36" ? planOf(r.plan).total : planOf(r.plan).mo * 36) : 0);

const Login = ({ onDone, a }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await http.post("/auth/login", { email, password });
      onDone(true);
    } catch (x) {
      setErr(formatApiError(x?.response?.data?.detail) || a.bad);
    }
  };
  const f = "w-full rounded-xl bg-white/5 border border-white/15 px-4 py-3 focus:outline-none focus:border-[#2DD4BF]";
  return (
    <form onSubmit={submit} className="max-w-sm mx-auto mt-24 space-y-4" data-testid="admin-login-form">
      <h1 className="text-4xl font-extrabold uppercase stretch-condensed">{a.login}</h1>
      <input className={f} type="email" required placeholder={a.email} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-email" />
      <input className={f} type="password" required placeholder={a.pw} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-password" />
      {err && <p className="text-sm text-red-300" data-testid="admin-login-error">{err}</p>}
      <button className="w-full rounded-full bg-[#2DD4BF] text-[#0B1F3A] py-3 font-semibold hover:bg-white transition-colors" data-testid="admin-login-btn">{a.enter}</button>
    </form>
  );
};

const exportCsv = (rows) => {
  const head = ["created_at", "name", "company", "email", "phone", "plan", "commitment", "status", "message"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [head.join(","), ...rows.map((r) => head.map((h) => esc(r[h])).join(","))].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  Object.assign(document.createElement("a"), { href: url, download: "adhesions.csv" }).click();
  URL.revokeObjectURL(url);
};

const Kpi = ({ label, value, id }) => (
  <div className="rounded-2xl border border-white/10 bg-[#0F2647] p-6" data-testid={id}>
    <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">{label}</p>
    <p className="mt-3 text-4xl font-extrabold stretch-condensed">{value}</p>
  </div>
);

const Dashboard = ({ a, onLogout }) => {
  const { t, lang } = useLang();
  const [rows, setRows] = useState([]);
  useEffect(() => {
    http.get("/adhesions").then((r) => setRows(Array.isArray(r.data) ? r.data : [])).catch(() => onLogout());
  }, [onLogout]);
  const stats = useMemo(() => {
    const counts = rows.reduce((m, r) => ({ ...m, [r.plan]: (m[r.plan] || 0) + 1 }), {});
    const top = Object.entries(counts).sort((x, y) => y[1] - x[1])[0]?.[0];
    return { total: rows.length, value: rows.reduce((s, r) => s + value36(r), 0), top: top ? t.pricing.plans[top].name : "—" };  }, [rows, t]);
  const setStatus = async (id, status) => {
    const { data } = await http.patch(`/adhesions/${id}`, { status });
    setRows((rs) => rs.map((r) => (r.id === id ? data : r)));
  };
  const remove = async (id) => {
    await http.delete(`/adhesions/${id}`);
    setRows((rs) => rs.filter((r) => r.id !== id));
    toast.success("OK");
  };
  return (
    <div className="mt-12" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <h1 className="text-4xl lg:text-6xl font-extrabold uppercase stretch-condensed">{a.title}</h1>
        <button onClick={() => exportCsv(rows)} data-testid="admin-export-csv" className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm hover:border-[#2DD4BF] hover:text-[#2DD4BF] transition-colors"><Download size={14} />CSV</button>
      </div>
      <div className="mt-8 grid sm:grid-cols-3 gap-4">
        <Kpi id="kpi-total" label={a.total} value={stats.total} />
        <Kpi id="kpi-value" label={a.value} value={money(stats.value, lang)} />
        <Kpi id="kpi-top" label={a.top} value={stats.top} />
      </div>
      <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-sm min-w-[760px]" data-testid="admin-table">
          <thead className="bg-[#0F2647] text-left font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
            <tr>{a.cols.map((c, i) => <th key={i} className="px-4 py-3 font-normal">{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">{a.empty}</td></tr>}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-white/10 align-top" data-testid={`admin-row-${r.id}`}>
                <td className="px-4 py-4 text-slate-300 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString(lang)}</td>
                <td className="px-4 py-4">
                  <p className="font-semibold">{r.name} · {r.company}</p>
                  <p className="text-slate-400">{r.email}{r.phone ? ` · ${r.phone}` : ""}</p>
                  {r.message && <p className="mt-1 text-slate-300 max-w-sm">{r.message}</p>}
                </td>
                <td className="px-4 py-4">{t.pricing.plans[r.plan]?.name}</td>
                <td className="px-4 py-4 text-slate-300">{r.commitment === "36" ? t.pricing.t36 : t.pricing.tnone}</td>
                <td className="px-4 py-4">
                  <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                    <SelectTrigger className="w-40" data-testid={`status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(a.status).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td className="px-4 py-4"><button onClick={() => remove(r.id)} data-testid={`delete-${r.id}`} className="text-slate-400 hover:text-red-300 transition-colors" aria-label="delete"><Trash2 size={16} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Admin() {
  const { lang } = useLang();
  const a = L[lang] || L.fr;
  const [authed, setAuthed] = useState(null);
  useEffect(() => {
    http.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);
  const logout = async () => {
    await http.post("/auth/logout").catch(() => null);
    setAuthed(false);
  };
  return (
    <div className="min-h-screen bg-[#0B1F3A] text-slate-50 pb-20">
      <header className="border-b border-white/10">
        <div className="max-w-[1440px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="admin-back"><Wordmark /></Link>
          <div className="flex items-center gap-3">
            <LangSwitch />
            <Link to="/" className="hidden sm:inline-flex items-center gap-1 text-sm text-slate-300 hover:text-white"><ArrowLeft size={14} />{a.back}</Link>
            {authed && <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-[#2DD4BF]"><LogOut size={14} />{a.out}</button>}
          </div>
        </div>
      </header>
      <div className="max-w-[1440px] mx-auto px-5 lg:px-10">
        {authed === false && <Login a={a} onDone={setAuthed} />}
        {authed && <Dashboard a={a} onLogout={logout} />}
      </div>
    </div>
  );
}
